/**
 * Registry de apps da Surf Monitor Platform.
 *
 * Fonte da verdade do que aparece na sidebar do shell.
 *
 * Para adicionar uma app:
 *   1. Adicione um App aqui
 *   2. Configure DNS *.stage.surf.com.br
 *   3. Garanta que a app aceita JWT via postMessage (useJwtFromShell)
 */

export type AppId =
  | 'gg-monitor'
  | 'ai-assistant'
  | 'recargas'
  | 'mvnos'
  | 'pagtel'
  | 'saude-apis'
  | 'callbox'
  | 'analytics'
  | 'rds'
  | 'ec2';

export type AppStatus =
  | 'live' // app independente em produção
  | 'wip' // em desenvolvimento (esta release)
  | 'legacy'; // ainda apontando para CX v3

export interface App {
  id: AppId;
  label: string;
  description: string;
  icon: string; // lucide-react icon name
  url: string; // URL completa (carregada em iframe)
  status: AppStatus;
  /** Roles que podem ver esta app na sidebar. Vazio = todos. */
  allowedRoles?: ('admin' | 'sre' | 'manager' | 'director')[];
}

export const APPS: readonly App[] = [
  {
    id: 'gg-monitor',
    label: 'GG Monitor',
    description: 'Observabilidade Nokia SAEGW',
    icon: 'Network',
    url: 'https://gg.stage.surf.com.br/',
    status: 'wip',
  },
  {
    id: 'ai-assistant',
    label: 'Assistente IA',
    description: '16 agents — investigação, predição, conhecimento',
    icon: 'Bot',
    url: 'https://ai.stage.surf.com.br/',
    status: 'wip',
  },
  {
    id: 'recargas',
    label: 'Recargas Live',
    description: 'Stream de recargas em tempo real',
    icon: 'Zap',
    url: 'https://cx.stage.surf.com.br/#/recargas',
    status: 'legacy',
  },
  {
    id: 'mvnos',
    label: 'MVNOs',
    description: 'Saúde e SLA por MVNO',
    icon: 'Building2',
    url: 'https://cx.stage.surf.com.br/#/mvno',
    status: 'legacy',
  },
  {
    id: 'pagtel',
    label: 'Pagtel Monitor',
    description: 'Investigação de negadas Pagtel',
    icon: 'AlertTriangle',
    url: 'https://cx.stage.surf.com.br/#/pagtel',
    status: 'legacy',
  },
  {
    id: 'saude-apis',
    label: 'Saúde APIs',
    description: 'Golden Signals (RED) + Service Map',
    icon: 'Activity',
    url: 'https://cx.stage.surf.com.br/#/services',
    status: 'legacy',
  },
  {
    id: 'callbox',
    label: 'Callbox',
    description: 'Call center realtime',
    icon: 'Phone',
    url: 'https://cx.stage.surf.com.br/#/callbox',
    status: 'legacy',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Funil eventoSVA + tendências',
    icon: 'BarChart3',
    url: 'https://cx.stage.surf.com.br/#/ciclo',
    status: 'legacy',
  },
  {
    id: 'rds',
    label: 'RDS Aurora',
    description: 'Locks, QPS, slow queries',
    icon: 'Database',
    url: 'https://cx.stage.surf.com.br/#/rds',
    status: 'legacy',
    allowedRoles: ['admin', 'sre'],
  },
  {
    id: 'ec2',
    label: 'EC2',
    description: 'Health dos workers',
    icon: 'Server',
    url: 'https://cx.stage.surf.com.br/#/ec2',
    status: 'legacy',
    allowedRoles: ['admin', 'sre'],
  },
] as const;

export function appsForRole(role: string): readonly App[] {
  return APPS.filter(
    (a) => !a.allowedRoles || a.allowedRoles.includes(role as never),
  );
}

export function findApp(id: AppId): App | undefined {
  return APPS.find((a) => a.id === id);
}
